






































































































import { diffDeep } from '@/utils/helpers';
import { mapActions, mapGetters } from 'vuex';
import { AxiosError } from 'axios';

import { ALForm } from '@/common/component.types';
import {
  IDocumentData,
  IQuoteDocumentData,
  IQuoteProductDocument,
  IQuoteProductDocumentData,
  QuoteProductDocument
} from '@/models';
import { FieldErrors, SystemError } from '@/models/error/error';
import { ISystemError, ISystemErrorData } from '@/models/error/error.types';

import mixins from '@/utils/mixins';
import authMixin from '@/mixins/auth';
import formRulesMixin from '@/mixins/file-upload-rules';
import { ISetQuoteProductDocumentTagsPayload } from '@/services/document';

import ALDialog from '../core/Dialog/ALDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';
import DocumentDetailTagPicker from '@/components/document/DocumentDetailTagPicker.vue';

const baseMixins = mixins(authMixin, formRulesMixin);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export enum FormType {
  All,
  Title,
  Tag
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'DocumentDetailFormDialog',
    components: {
      ALDialog,
      DragDropFileInput,
      ErrorAlert,
      DocumentDetailTagPicker
    },

    data() {
      return {
        FormType,
        dialog: false,
        loading: false,
        isEdit: false,
        formType: FormType.All,
        isFormValid: false,

        documentData: {
          quote_document: {
            document: {} as IDocumentData
          } as IQuoteDocumentData
        } as IQuoteProductDocumentData,

        tags: [] as number[],
        error: null as ISystemError | null
      };
    },

    computed: {
      ...mapGetters({
        getQuoteProductDocumentById: 'document/getQuoteProductDocumentById',
        getLatestQuoteProductDocument: 'document/getLatestQuoteProductDocument'
      }),

      formTitle(): string {
        let title = 'Create Marketing Document';

        switch (this.formType) {
          case FormType.Title:
            title = 'Edit Document Title';
            break;

          case FormType.Tag:
            title = 'Edit Document Tags';
            break;

          default:
            break;
        }
        return title;
      },

      showTitleField(): boolean {
        return this.formType == FormType.Title || this.formType == FormType.All;
      },

      showTagField(): boolean {
        return this.formType == FormType.Tag || this.formType == FormType.All;
      },

      showFileField(): boolean {
        return this.formType == FormType.All;
      }
    },

    methods: {
      ...mapActions({
        createQuoteProductDocument: 'document/createQuoteProductDocument',
        updateQuoteProductDocument: 'document/updateQuoteProductDocument',
        setQuoteProductDocumentTags: 'document/setQuoteProductDocumentTags'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      open(document?: IQuoteProductDocument, formType = FormType.All) {
        this.clearErrors();
        this.resetFormValidation();
        this.formType = formType;
        this.isEdit = !!document;

        this.documentData = this.isEdit
          ? new QuoteProductDocument(document!)
          : ({
              quote_document: {
                document: { tags: [] } as IDocumentData
              } as IQuoteDocumentData
            } as IQuoteProductDocumentData);

        this.tags = this.documentData.quote_document!.document!.tags!.map(
          tag => tag.id!
        );

        this.dialog = true;
      },

      close() {
        this.dialog = false;
      },

      async save() {
        this.clearErrors();
        const valid = this.validateForm();

        if (valid) {
          let documentInstance: IQuoteProductDocument | null = null;

          try {
            documentInstance = new QuoteProductDocument(this.documentData);
          } catch (error) {
            if (SystemError.isSystemError(error)) {
              this.error = error;
            }
          }

          if (documentInstance != null) {
            this.loading = true;

            const saveAction = this.isEdit
              ? this.updateQuoteProductDocument
              : this.createQuoteProductDocument;

            let payload = documentInstance;

            if (this.isEdit) {
              payload = diffDeep(
                documentInstance,
                this.getQuoteProductDocumentById(documentInstance.id),
                true
              );
            }

            try {
              await saveAction(payload);
              await this.setQuoteProductDocumentTags({
                quoteId:
                  documentInstance.id ?? this.getLatestQuoteProductDocument!.id,
                tags: this.tags
              } as ISetQuoteProductDocumentTagsPayload);
              this.close();
            } catch (err) {
              const error = err as AxiosError;
              const systemErrorData: ISystemErrorData = {
                name: error.name,
                message: error.message
              };

              if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                systemErrorData.message = error.response.statusText;
                if (error.response.data.detail) {
                  systemErrorData.detail = error.response.data.detail;
                } else if (FieldErrors.isFieldError(error.response.data)) {
                  systemErrorData.fieldErrors = new FieldErrors(
                    error.response.data
                  );
                }
              } else if (error.request) {
                // The request was made but no response was received (Can happen on 500 error)
                systemErrorData.detail =
                  'There was an error in processing your request. Please check with IT Admin';
              }

              this.error = new SystemError(systemErrorData);
            } finally {
              this.loading = false;
            }
          }
        }
      },

      clearErrors() {
        this.error = null;
      }
    }
  });
